import { RevealPanel } from './revealPanel';
import { throttle, debounce } from 'lodash';

const SCROLL_DOWN_CLASS = "scroll-down";
const BODY_SCROLL_DOWN_CLASS = "nav-hidden";
const TAB_PANEL_ATTRIBUTE = "data-nav-tabpanel";

const NAV_MENU_ATTRIBUTE = "data-menu";
const NAV_MENU_TRIGGER_ATTRIBUTE = "data-nav-menu-trigger";
const MOBILE_UTILITY_NAV_ATTRIBUTE = "data-mobile-util-nav";
const TOP_PAGE_BUFFER = 200;
const TOP_PAGE_ATTRIBUTE = "data-above-fold";
const KEYCODE_ESC = 27;

export class MainNavigation {
    navContainer: HTMLElement;
    navPanelTriggers: HTMLButtonElement[];
    navPanel: HTMLElement;
    tabPanels: RevealPanel[];
    defaultPanels: RevealPanel[];
    mobileUtilNav: HTMLElement;
    scrollPosition: number;
    scrollDelta: number;
    navPanelIsOpen: boolean;
    totalBuffer: number;
    pageHeight: number;
    constructor(navContainer: HTMLElement) {
        this.navContainer = navContainer;
        this.navPanel = document.querySelector(`[${NAV_MENU_ATTRIBUTE}]`) as HTMLElement;
        this.navPanelTriggers = Array.from(document.querySelectorAll(`[${NAV_MENU_TRIGGER_ATTRIBUTE}]`)) as HTMLButtonElement[];
        this.tabPanels = [];
        this.defaultPanels = [];
        this.mobileUtilNav = document.querySelector(`[${MOBILE_UTILITY_NAV_ATTRIBUTE}]`) as HTMLElement;
        this.scrollPosition = window.scrollY;
        this.scrollDelta = 0;
        this.navPanelIsOpen = false;
        this.totalBuffer = this.navContainer.getBoundingClientRect().bottom + TOP_PAGE_BUFFER;
        this.pageHeight = window.innerHeight;

        const tabNavPanels = Array.from(document.querySelectorAll(`[${TAB_PANEL_ATTRIBUTE}]`)) as HTMLElement[];

        tabNavPanels.forEach(panelEl => {
            const isDefault = panelEl.getAttribute(`${TAB_PANEL_ATTRIBUTE}`) == "default";
            const tabPanel = new RevealPanel(panelEl);
            if (isDefault) {
                this.defaultPanels.push(tabPanel);
            }
            else {
                this.tabPanels.push(tabPanel);
            }
        });

        this.setupPanels();

        document.body.addEventListener('click', (e: MouseEvent) => {
            if (!this.navContainer.contains(e.target as HTMLElement)) {
                this.tabPanels.forEach(panel => {
                    if (panel.isOpen) {
                        panel.closePanel();
                    }
                });
            }
        });

        window.addEventListener('scroll', throttle(this.handleScroll, 100));
        window.addEventListener('resize', debounce(this.handleResize.bind(this), 500));
        this.navContainer.addEventListener('keydown', this.handleKeydown);
        this.navContainer.addEventListener('focusout', this.handleFocusChange);

        setTimeout(() => {
            this.handleResize();
        }, 0);

        if (!!this.navPanel && !!this.navPanelTriggers) {
            this.navPanelTriggers.forEach(trigger => {
                trigger.addEventListener('click', () => {
                    this.toggleNavPanel();
                });
            })
        }
    }

    closeEveryPanel = () => {
        this.tabPanels.forEach(panel => {
            if (panel.isOpen) {
                panel.closePanel();
            }
        });
    }

    handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' || e.keyCode === KEYCODE_ESC) {
            if (this.navPanelIsOpen) {
                this.toggleNavPanel();
            }
            this.closeEveryPanel();
        }
    }

    handleFocusChange = (e: FocusEvent) => {
        // Tab outside of nav to close
        const newFocusedElement = e.relatedTarget as HTMLElement;
        if (!(this.navContainer.contains(newFocusedElement) || this.navPanel.contains(newFocusedElement))) {
            if (newFocusedElement != null) {
                this.closeEveryPanel();
                if (this.navPanelIsOpen) this.toggleNavPanel();
            }
        }
    }

    handleScroll = () => {
        const lastScrollPosition = this.scrollPosition;
        const lastScrollDelta = this.scrollDelta;
        this.scrollPosition = window.scrollY;
        this.scrollDelta = this.scrollPosition - lastScrollPosition;

        document.body.setAttribute(TOP_PAGE_ATTRIBUTE, `${(this.scrollPosition < this.pageHeight)}`);

        if (lastScrollDelta > 0 && this.scrollDelta > 0 && this.scrollPosition > this.totalBuffer) {
            this.navContainer.classList.add(SCROLL_DOWN_CLASS);
            document.body.classList.add(BODY_SCROLL_DOWN_CLASS);
        }
        else {
            this.navContainer.classList.remove(SCROLL_DOWN_CLASS);
            document.body.classList.remove(BODY_SCROLL_DOWN_CLASS);
        }
    }

    setupPanels = () => {
        this.tabPanels.forEach(panel => {
            panel.on('open', () => {
                [...this.tabPanels, ...this.defaultPanels].forEach(otherPanel => {
                    if (otherPanel != panel && otherPanel.isOpen) {
                        otherPanel.closePanel();
                    }
                });
            });
            panel.on('close', () => {
                const openPanels = this.tabPanels.filter(panel => panel.isOpen);
                const closedDefaultPanels = this.defaultPanels.filter(panel => !panel.isOpen);
                if (openPanels.length == 0 && closedDefaultPanels.length > 0) {
                    closedDefaultPanels.forEach(panel => {
                        panel.openPanel();
                    });
                }
            });
        });
    }

    handleResize() {
        const newHeight = window.innerHeight;
        const delta = Math.abs(newHeight - this.pageHeight);
        this.pageHeight = newHeight;

        if (delta > 200 && this.navPanelIsOpen) {
            this.toggleNavPanel();
        }

        if (!this.navPanelIsOpen) {
            document.body.style.setProperty('--nav-clearance', `${this.navContainer.offsetHeight}px`);
            document.body.style.setProperty('--mobile-util-nav-height', `${this.mobileUtilNav.offsetHeight}px`);
            this.totalBuffer = this.navContainer.getBoundingClientRect().bottom + TOP_PAGE_BUFFER;
        }
    }

    toggleNavPanel() {
        if (this.navPanelIsOpen) {
            this.navPanel.setAttribute('aria-hidden', 'true');
            this.navPanel.setAttribute('inert', '');
            this.navPanelTriggers.forEach(trigger => {
                trigger.setAttribute('aria-expanded', 'false');
            });
            this.navPanel.classList.remove('nav-open');
            [...this.tabPanels].forEach(panel => {
                if (panel.isOpen) panel.closePanel();
            });
            this.navPanelIsOpen = false;
        }
        else {
            this.navPanel.setAttribute('aria-hidden', 'false');
            this.navPanel.removeAttribute('inert');
            this.navPanelTriggers.forEach(trigger => {
                trigger.setAttribute('aria-expanded', 'true');
            });
            this.navPanelIsOpen = true;
            setTimeout(() => {
                this.navPanel.classList.add('nav-open');
            }, 500);
        }
    }
}